/* FarpointAstro mask engine (JS port of mask_gen.py). Works in Node and browser.
   Boolean ops via polygon-clipping (Node: require; browser: window.polygonClipping). */
(function(root,factory){
  if(typeof module==="object"&&module.exports){ module.exports=factory(require("polygon-clipping")); }
  else { root.MaskEngine=factory(root.polygonClipping); }
})(typeof self!=="undefined"?self:this, function(pc){
"use strict";
// robust boolean wrappers: polygon-clipping occasionally throws "Unable to complete
// output ring" on exact tangencies. Retry with value-based micro-jitter that preserves
// ring closure (identical points -> identical offset) but breaks degenerate alignments.
const _jp=(p,eps)=>[p[0]+eps*Math.sin(p[0]*12.9898+p[1]*4.1414), p[1]+eps*Math.cos(p[0]*78.233+p[1]*1.618)];
const _jit=(g,eps)=>g.map(poly=>poly.map(ring=>ring.map(p=>_jp(p,eps))));
const _shift=(g,dx,dy)=>g.map(poly=>poly.map(ring=>ring.map(p=>[p[0]+dx,p[1]+dy])));
function safeOp(op,a,b){
  try{const r=op(a,b); if(r) return r;}catch(e){}
  for(const eps of [1e-7,3e-7,1e-6,3e-6,1e-5,3e-5,1e-4,3e-4]){
    try{const r=op(a,_jit(b,eps)); if(r) return r;}catch(e){}
    try{const r=op(_jit(a,eps),b); if(r) return r;}catch(e){}
    try{const r=op(_jit(a,eps),_jit(b,eps)); if(r) return r;}catch(e){}
  }
  // last resort: translate both by an irrational offset, op, translate back
  for(const d of [1.7e-3,-2.3e-3,5.1e-3]){
    try{const r=op(_shift(a,d,d*0.6),_shift(b,d,d*0.6)); if(r) return _shift(r,-d,-d*0.6);}catch(e){}
  }
  return [];
}
const IN=(a,b)=>safeOp(pc.intersection,a,b);
const DF=(a,b)=>safeOp(pc.difference,a,b);
const UN=(a,b)=>safeOp(pc.union,a,b);
const D2R=Math.PI/180;
const pol=(r,d)=>[r*Math.cos(d*D2R), r*Math.sin(d*D2R)];
const rot=(p,d)=>{const a=d*D2R,c=Math.cos(a),s=Math.sin(a);return [p[0]*c-p[1]*s,p[0]*s+p[1]*c];};
const hyp=(x,y)=>Math.hypot(x,y);

function bulgeBetween(P0,P1,C,ccw){
  let a0=Math.atan2(P0[1]-C[1],P0[0]-C[0]),a1=Math.atan2(P1[1]-C[1],P1[0]-C[0]),d=a1-a0;
  if(ccw){while(d<=0)d+=2*Math.PI; while(d>2*Math.PI)d-=2*Math.PI;}
  else  {while(d>=0)d-=2*Math.PI; while(d<-2*Math.PI)d+=2*Math.PI;}
  return Math.tan(d/4);
}
function arcBulge(A,B,C,through){
  let a0=Math.atan2(A[1]-C[1],A[0]-C[0]),a1=Math.atan2(B[1]-C[1],B[0]-C[0]),r=hyp(A[0]-C[0],A[1]-C[1]);
  let base=((a1-a0)%(2*Math.PI)+2*Math.PI)%(2*Math.PI), best=null;
  for(const d of [base, base-2*Math.PI]){
    let ma=a0+d/2, mp=[C[0]+r*Math.cos(ma),C[1]+r*Math.sin(ma)], dist=hyp(mp[0]-through[0],mp[1]-through[1]);
    if(best===null||dist<best[0]) best=[dist,d];
  }
  return Math.tan(best[1]/4);
}
// contour = [{x,y,b}] closed; b applies to edge to next vertex (bulge=tan(theta/4))
function flatten(contour,res){ // -> [[x,y],...] ring
  res=res||14; const n=contour.length, out=[];
  for(let i=0;i<n;i++){
    const a=contour[i], b=contour[(i+1)%n];
    if(Math.abs(a.b)<1e-9){ out.push([a.x,a.y]); continue; }
    const ang=4*Math.atan(a.b), ch=hyp(b.x-a.x,b.y-a.y);
    if(ch<1e-12){ out.push([a.x,a.y]); continue; }
    const r=ch/(2*Math.sin(Math.abs(ang)/2)), mx=(a.x+b.x)/2,my=(a.y+b.y)/2,dx=(b.x-a.x)/ch,dy=(b.y-a.y)/ch;
    const h=Math.sqrt(Math.max(r*r-(ch/2)**2,0))*(ang>0?1:-1), cx=mx-dy*h,cy=my+dx*h;
    let A0=Math.atan2(a.y-cy,a.x-cx),A1=Math.atan2(b.y-cy,b.x-cx);
    if(ang>0&&A1<A0)A1+=2*Math.PI; if(ang<0&&A1>A0)A1-=2*Math.PI;
    for(let j=0;j<res;j++){const t=A0+(A1-A0)*j/res; out.push([cx+r*Math.cos(t),cy+r*Math.sin(t)]);}
  }
  return out;
}
function polyArea(ring){let s=0,n=ring.length;for(let i=0;i<n;i++){s+=ring[i][0]*ring[(i+1)%n][1]-ring[(i+1)%n][0]*ring[i][1];}return Math.abs(s)/2;}
function centroid(ring){let x=0,y=0;for(const p of ring){x+=p[0];y+=p[1];}return [x/ring.length,y/ring.length];}
// reject tiny/thin fragments: thickness approx 2*area/perimeter
function outBound(ring,Rmax){for(const p of ring){if(hyp(p[0],p[1])>Rmax)return true;}return false;}
function thinFrag(ring,minFrag,minThk){const A=polyArea(ring);if(A<minFrag)return true;
  // reject by AVERAGE width (area / longest dimension): true uniform slivers are dropped,
  // but full-width slits that merely pinch at a boundary (e.g. grazing the separating ring)
  // or are short are kept.
  let ext=0,n=ring.length;
  for(let i=0;i<n;i++)for(let j=i+1;j<n;j++){const d=hyp(ring[i][0]-ring[j][0],ring[i][1]-ring[j][1]);if(d>ext)ext=d;}
  if(ext<1e-9)return true;
  return (A/ext)<minThk;}

function refit(ring,circles,cen,tol){ // ring [[x,y]], circles [[cx,cy,r]] -> contour [{x,y,b}]
  tol=tol||4e-3; const n=ring.length, tag=[];
  for(const v of ring){let ci=-1;for(let j=0;j<circles.length;j++){const[cx,cy,r]=circles[j];if(Math.abs(hyp(v[0]-cx,v[1]-cy)-r)<tol){ci=j;break;}}tag.push(ci);}
  let start=0;for(let i=0;i<n;i++){if(tag[i]!==tag[(i-1+n)%n]){start=i;break;}}
  const out=[];let count=0,idx=start;
  while(count<n){
    const j=idx,cj=tag[j];
    if(cj>=0){
      let steps=0;while(steps<n-1&&tag[(j+steps+1)%n]===cj)steps++;
      if(steps===0){out.push({x:ring[j][0],y:ring[j][1],b:0});count++;idx=(j+1)%n;}
      else{const A=ring[j],B=ring[(j+steps)%n],[cx,cy,r]=circles[cj];
        let dx=cen[0]-cx,dy=cen[1]-cy,dm=hyp(dx,dy)||1,through=[cx+r*dx/dm,cy+r*dy/dm];
        out.push({x:A[0],y:A[1],b:arcBulge(A,B,[cx,cy],through)});count+=steps;idx=(j+steps)%n;}
    } else {out.push({x:ring[j][0],y:ring[j][1],b:0});count++;idx=(j+1)%n;}
  }
  return out;
}
// analytic clippers (classic normals)
function clipHP(verts,a,b,c){const out=[],n=verts.length;for(let i=0;i<n;i++){const cur=verts[i],nx=verts[(i+1)%n];const fc=a*cur[0]+b*cur[1]+c,fn=a*nx[0]+b*nx[1]+c;if(fc>=-1e-12)out.push(cur);if((fc>=0)!==(fn>=0)){const t=fc/(fc-fn);out.push([cur[0]+t*(nx[0]-cur[0]),cur[1]+t*(nx[1]-cur[1])]);}}return out;}
function segCircle(A,B,cx,cy,rad){const dx=B[0]-A[0],dy=B[1]-A[1],a=dx*dx+dy*dy;if(a<1e-18)return null;const b=2*((A[0]-cx)*dx+(A[1]-cy)*dy),c=(A[0]-cx)**2+(A[1]-cy)**2-rad*rad,disc=b*b-4*a*c;if(disc<0)return null;const sq=Math.sqrt(disc);for(const t of [(-b-sq)/(2*a),(-b+sq)/(2*a)].sort((x,y)=>x-y)){if(t>=-1e-9&&t<=1+1e-9)return [A[0]+t*dx,A[1]+t*dy];}return null;}
function clipCircleIn(verts,cx,cy,rad){const out=[],n=verts.length;for(let i=0;i<n;i++){const cur=verts[i],nx=verts[(i+1)%n];const fc=rad*rad-((cur[0]-cx)**2+(cur[1]-cy)**2),fn=rad*rad-((nx[0]-cx)**2+(nx[1]-cy)**2);if(fc>=-1e-9)out.push(cur);if((fc>=0)!==(fn>=0)){const ip=segCircle(cur,nx,cx,cy,rad);if(ip)out.push(ip);}}return out;}

// ---- shell ----
const SLOT_W=0.375, SLOT_ANGLES=[90,210,330], CREST_R=0.438, FILLET_R=0.5;
const DIV_H=0.188, DIV_V=0.188, ANGLE_FROM_VERT=30;
function tabPoints(Rb,rc,rf){
  const Cc=[Rb,0], fx=((Rb+rf)**2-(rc+rf)**2+Rb*Rb)/(2*Rb), fy=Math.sqrt(Math.max((Rb+rf)**2-fx*fx,0));
  const Cf_hi=[fx,fy],Cf_lo=[fx,-fy];
  const T1_hi=[Rb*fx/(Rb+rf),Rb*fy/(Rb+rf)],T1_lo=[T1_hi[0],-T1_hi[1]];
  const vx=(fx-Rb)/(rc+rf),vy=fy/(rc+rf),T2_hi=[Rb+rc*vx,rc*vy],T2_lo=[T2_hi[0],-T2_hi[1]];
  return {T1_lo,T2_lo,T2_hi,T1_hi,Cf_lo,Cf_hi,Cc};
}
function outlineVerts(Rb,rc,rf){rc=rc||CREST_R;rf=rf||FILLET_R;
  const t=tabPoints(Rb,rc,rf),V=[];
  for(const C of SLOT_ANGLES){
    const t1lo=rot(t.T1_lo,C),t2lo=rot(t.T2_lo,C),t2hi=rot(t.T2_hi,C),t1hi=rot(t.T1_hi,C);
    const cflo=rot(t.Cf_lo,C),cfhi=rot(t.Cf_hi,C),cc=rot(t.Cc,C);
    V.push({x:t1lo[0],y:t1lo[1],b:bulgeBetween(t1lo,t2lo,cflo,false)});
    V.push({x:t2lo[0],y:t2lo[1],b:bulgeBetween(t2lo,t2hi,cc,true)});
    V.push({x:t2hi[0],y:t2hi[1],b:bulgeBetween(t2hi,t1hi,cfhi,false)});
    V.push({x:t1hi[0],y:t1hi[1],b:0});
  }
  for(let i=0;i<3;i++){const idx=i*4+3,P0=[V[idx].x,V[idx].y],nx=(i+1)*4%V.length,P1=[V[nx].x,V[nx].y];V[idx].b=bulgeBetween(P0,P1,[0,0],true);}
  return V;
}
function slotContour(r_in,r_out,C,w){w=w||SLOT_W;const u=pol(1,C),n=[-u[1],u[0]],hw=w/2;
  const A=[r_in*u[0],r_in*u[1]],B=[r_out*u[0],r_out*u[1]];
  const Al=[A[0]+hw*n[0],A[1]+hw*n[1]],Bl=[B[0]+hw*n[0],B[1]+hw*n[1]],Br=[B[0]-hw*n[0],B[1]-hw*n[1]],Ar=[A[0]-hw*n[0],A[1]-hw*n[1]];
  return [{x:Al[0],y:Al[1],b:0},{x:Bl[0],y:Bl[1],b:bulgeBetween(Bl,Br,B,false)},{x:Br[0],y:Br[1],b:0},{x:Ar[0],y:Ar[1],b:bulgeBetween(Ar,Al,A,false)}];
}
// full-circle contour as two 180-deg arc segments (b=tan(theta/4)=1)
const circleContour=(r)=>[{x:r,y:0,b:1},{x:-r,y:0,b:1}];
// polygon helpers for boolean (polygon-clipping geometry = [ [ring,...] ])
const circlePoly=(cx,cy,r,nseg)=>{nseg=nseg||128;const ring=[];for(let i=0;i<nseg;i++){const a=2*Math.PI*i/nseg;ring.push([cx+r*Math.cos(a),cy+r*Math.sin(a)]);}ring.push(ring[0].slice());return [ring];};
function stadiumPoly(A,B,rad,nseg){nseg=nseg||64;const dx=B[0]-A[0],dy=B[1]-A[1],L=hyp(dx,dy),ux=dx/L,uy=dy/L,nx=-uy,ny=ux,ring=[];
  let a0=Math.atan2(ny,nx);for(let i=0;i<=nseg;i++){const a=a0 - Math.PI*i/nseg;ring.push([B[0]+rad*Math.cos(a),B[1]+rad*Math.sin(a)]);} // cap at B
  a0=Math.atan2(-ny,-nx);for(let i=0;i<=nseg;i++){const a=a0 - Math.PI*i/nseg;ring.push([A[0]+rad*Math.cos(a),A[1]+rad*Math.sin(a)]);} // cap at A
  ring.push(ring[0].slice());return [ring];}
function wedgePoly(r0,r1,ac,ah,nseg){nseg=nseg||48;const ring=[];
  for(let i=0;i<=nseg;i++){const a=(ac-ah+2*ah*i/nseg)*D2R;ring.push([r1*Math.cos(a),r1*Math.sin(a)]);}
  for(let i=0;i<=nseg;i++){const a=(ac+ah-2*ah*i/nseg)*D2R;ring.push([r0*Math.cos(a),r0*Math.sin(a)]);}
  ring.push(ring[0].slice());return [ring];}

// shared classic Bahtinov grating emitter. Pushes slit contours into `out`.
// disc=outer boundary polygon (radius Rg). innerPoly=optional center-hole polygon to subtract.
// slotUnion=optional grommet-slot keepout. circles=arc-refit references. Rg used for rim arc refit.
function emitClassicGrating(out,R,Rg,period,hw,minFrag,disc,innerPoly,slotUnion,circles,angDeg,minThk,divH,divV){
  const dH=divH!=null?divH:DIV_H, dV=divV!=null?divV:DIV_V;
  const REG={low:[[0,-1,-dH]],ul:[[-1,0,-dV],[0,1,-dH]],ur:[[1,0,-dV],[0,1,-dH]]};
  function emit(P0,u,reg){const nrm=[-u[1],u[0]],L=R+1;
    const P=(t,off)=>[P0[0]+t*u[0]+off*nrm[0],P0[1]+t*u[1]+off*nrm[1]];
    let verts=[P(-L,hw),P(L,hw),P(L,-hw),P(-L,-hw)];
    for(const[a,b,c] of REG[reg]) verts=clipHP(verts,a,b,c);
    if(verts.length<3) return;
    let geom=[[[...verts.map(v=>[v[0],v[1]]),verts[0]]]];
    geom=IN(geom,disc);
    if(innerPoly) geom=DF(geom,innerPoly);
    if(slotUnion) geom=DF(geom,slotUnion);
    if(!geom||!geom.length) return;
    for(const poly of geom){const ring=poly[0].slice(0,-1); if(thinFrag(ring,minFrag,minThk)||outBound(ring,Rg+0.06)) continue;
      out.push({role:"slit",verts:refit(ring,circles,centroid(ring))});}
  }
  const th=(angDeg||ANGLE_FROM_VERT)*D2R;
  let k=0;while(k*period<=R+1){const xs=k===0?[0]:[k*period,-k*period];for(const sx of xs)emit([sx,0],[0,1],"low");k++;}
  const dR=[Math.sin(th),Math.cos(th)],perpR=[-dR[1],dR[0]],a0=dV*perpR[0]+dH*perpR[1];
  let m=-Math.floor(2*R/period)-2;while(m*period<=2*R+1){const off=a0+m*period,P0=[off*perpR[0],off*perpR[1]];emit(P0,dR,"ur");emit([-P0[0],P0[1]],[-dR[0],dR[1]],"ul");m++;}
}

function emitTriGrating(out,R,Rg,r_in_g,slotUnion,extraCircles,period,hw,slitW,minFrag,minThk,o){
  const triAngle=o.triAngleDeg!=null?o.triAngleDeg:10, rotDeg=o.rotDeg!=null?o.rotDeg:90;
  const zoneGap=o.zoneGap!=null?o.zoneGap:Math.max(slitW*1.5,0.15);
  const r_out_g=Rg, mid=(r_in_g+r_out_g)/2, innerTop=mid-zoneGap/2, outerBot=mid+zoneGap/2;
  const circles=[[0,0,r_in_g],[0,0,innerTop],[0,0,outerBot],[0,0,r_out_g],...(extraCircles||[])];
  const disc=circlePoly(0,0,Rg,256);
  const annulus=DF(disc,circlePoly(0,0,r_in_g,200));
  const spineW=o.spineW!=null?o.spineW:0.2, Lspan=r_out_g+1;
  function spacerRect(phiDeg){const a=phiDeg*D2R,d=[Math.cos(a),Math.sin(a)],nn=[-d[1],d[0]],hw2=spineW/2;
    const P=(t,oo)=>[t*d[0]+oo*nn[0],t*d[1]+oo*nn[1]];const ring=[P(0,-hw2),P(Lspan,-hw2),P(Lspan,hw2),P(0,hw2)];ring.push(ring[0].slice());return [ring];}
  function addRegion(sink,rectRing,region){let geom=IN([[rectRing]],region); if(slotUnion) geom=DF(geom,slotUnion);
    if(!geom||!geom.length)return;
    for(const poly of geom){const ring=poly[0].slice(0,-1); if(thinFrag(ring,minFrag,minThk)||outBound(ring,Rg+0.06))continue; sink.push({role:"slit",verts:refit(ring,circles,centroid(ring))});}}
  function fill(sink,region,u){const perp=[-u[1],u[0]];let m=-Math.floor(2*R/period)-2;
    while(m*period<=2*R+1){const oo=(m+0.5)*period,P0=[oo*perp[0],oo*perp[1]];
      const nrm=perp,L=2*R+2,P=(t,off)=>[P0[0]+t*u[0]+off*nrm[0],P0[1]+t*u[1]+off*nrm[1]];
      const rectRing=[P(-L,hw),P(L,hw),P(L,-hw),P(-L,-hw)]; rectRing.push(rectRing[0].slice());
      addRegion(sink,rectRing,region); m++;}}
  const wedgeReg=(r0,r1,ac,ah)=>{let reg=IN([wedgePoly(r0,r1,ac,ah)],annulus); reg=DF(reg,spacerRect(ac-ah)); reg=DF(reg,spacerRect(ac+ah)); return reg;};
  // Generate ONE group, then rotate by 120/240 deg for guaranteed perfect 3-fold symmetry
  // (all keep-outs — slots, spacers, rings, hub — are themselves 3-fold symmetric, so this is exact).
  const tmp=[], g=rotDeg;
  fill(tmp,wedgeReg(r_in_g,innerTop,180+g,60), pol(1,g));
  fill(tmp,wedgeReg(outerBot,r_out_g,30+g,30), pol(1,g+triAngle));
  fill(tmp,wedgeReg(outerBot,r_out_g,-30+g,30), pol(1,g-triAngle));
  const rc=(c,deg)=>({role:c.role,verts:c.verts.map(v=>{const p=rot([v.x,v.y],deg);return{x:p[0],y:p[1],b:v.b};})});
  for(const c of tmp){out.push(c); out.push(rc(c,120)); out.push(rc(c,240));}
}

// ---- engraved website text (baked outline polygons, font-independent) ----
const TEXT_FONT=(typeof FP_FONT!=="undefined")?FP_FONT:{adv:{},rings:{}};
// place the baked text on an arc in the rim band [Rinner,Router], centered at centerDeg,
// letters upright with tops pointing radially outward; emits role:"engrave" contours.
function layoutText(str){
  const F=TEXT_FONT; let x=0; const rings=[];
  for(const ch of String(str)){ const g=F.rings[ch], a=(F.adv[ch]!=null?F.adv[ch]:(F.adv[" "]||0.5));
    if(g) for(const ring of g) rings.push(ring.map(p=>[p[0]+x,p[1]]));
    x+=a; }
  return {W:x,rings};
}
function emitArcText(out,Router,Rinner,centerDeg,str,flip){
  if(!str||!String(str).length) return;
  const band=Router-Rinner, cap=Math.min(0.55*band,0.22); if(cap<0.045) return;
  const scale=cap/0.72, mid=(Router+Rinner)/2, cA=centerDeg*D2R;
  const L=layoutText(str), Wem=L.W; if(Wem<=0) return;
  const rb=flip?(mid+0.255*scale):(mid-0.255*scale);
  const warp=(xe,ye)=>{const s=(xe-Wem/2)*scale, r=flip?(rb-ye*scale):(rb+ye*scale), th=cA+(flip?1:-1)*s/rb; return [r*Math.cos(th),r*Math.sin(th)];};
  for(const ring of L.rings) out.push({role:"engrave",verts:ring.map(p=>{const w=warp(p[0],p[1]);return{x:w[0],y:w[1],b:0};})});
}

function finalize(contours,meta,o){
  // de-dupe near-identical contours (defensive against rare duplicate boolean output)
  const seen=[], keep=[];
  for(const c of contours){
    let cx=0,cy=0,per=0;const v=c.verts,n=v.length;
    for(let i=0;i<n;i++){cx+=v[i].x;cy+=v[i].y;const b=v[(i+1)%n];per+=hyp(b.x-v[i].x,b.y-v[i].y);}
    cx/=n;cy/=n; const key=[cx,cy,per,n];
    let dup=false;
    for(const k of seen){ if(k[3]===n&&Math.abs(k[0]-cx)<2e-3&&Math.abs(k[1]-cy)<2e-3&&Math.abs(k[2]-per)<2e-3){dup=true;break;} }
    if(dup&&c.role==="slit") continue;
    seen.push(key); keep.push(c);
  }
  // engraved website text on the rim (engrave layer)
  emitArcText(keep, meta.R, meta.Rg, 45, "www.FarpointAstro.com", false);
  if(o&&o.customText&&String(o.customText).trim()) emitArcText(keep, meta.R, meta.Rg, 270, String(o.customText).trim(), true);
  let L=0;
  for(const c of keep){if(c.role==="engrave")continue;const f=flatten(c.verts,24);for(let i=0;i<f.length;i++){const a=f[i],b=f[(i+1)%f.length];L+=hyp(a[0]-b[0],a[1]-b[1]);}}
  meta.cutLengthIn=L;
  const R=meta.R; meta.areaBBoxSqIn=(2*R)*(2*R); meta.areaDiscSqIn=Math.PI*R*R;
  return {contours:keep, meta};
}

function genMask(o){
  o=o||{};
  const maskType=o.maskType||"classic";
  const edge=o.edgeMargin!=null?o.edgeMargin:0.375;
  let period=o.periodIn, slitW=o.slitW;
  if(period==null) period=(o.focalMM/(o.bahtinovFactor||150))/25.4;
  if(slitW==null) slitW=period*(1/(1+(o.stemSlit!=null?o.stemSlit:1)));
  const hw=slitW/2, minFrag=(slitW/4)*slitW, minThk=Math.max(0.2*slitW,0.02);
  const cbW=o.centerBarW!=null?o.centerBarW:2*DIV_H, divH=cbW/2, divV=cbW/2;
  const contours=[]; // each {role, verts:[{x,y,b}]}

  // ---- SCT type: plain disc, center hole over secondary, classic grating, NO grommet slots ----
  if(maskType==="sct"||maskType==="sct_tri"){
    const fitTol=o.fitTol!=null?o.fitTol:2/25.4;
    const castingID=o.castingID!=null?o.castingID:9.25;
    const secondaryOD=o.secondaryOD!=null?o.secondaryOD:0;
    const Rs=(castingID-fitTol)/2;
    const holeR=secondaryOD>0?(secondaryOD+fitTol)/2:0;
    const rim=o.rimSCT!=null?o.rimSCT:0.18, Rg=Rs-rim;
    contours.push({role:"outline",verts:circleContour(Rs)});
    if(holeR>0) contours.push({role:"hole",verts:circleContour(holeR)});
    const ringW=holeR>0?(o.ringW!=null?o.ringW:0.2):0;
    const ringR=holeR>0?holeR+ringW:0;
    const angDeg=o.angleFromVert!=null?o.angleFromVert:ANGLE_FROM_VERT;
    if(maskType==="sct_tri"){
      const r_in_g=ringR>0?ringR:(o.hubMin!=null?o.hubMin:0.28);
      emitTriGrating(contours,Rs,Rg,r_in_g,null,[],period,hw,slitW,minFrag,minThk,o);
    } else {
      const disc=circlePoly(0,0,Rg,256), innerPoly=ringR>0?circlePoly(0,0,ringR,200):null;
      const circles=[[0,0,Rg]]; if(ringR>0) circles.push([0,0,ringR]);
      emitClassicGrating(contours,Rs,Rg,period,hw,minFrag,disc,innerPoly,null,circles,angDeg,minThk,divH,divV);
    }
    return finalize(contours,{R:Rs,Rg,period,slitW,type:maskType,maskOD:2*Rs,holeOD:2*holeR,ringOD:2*ringR,fitTol,castingID,secondaryOD,angleFromVert:angDeg},o);
  }

  // ---- round masks (classic / tri_v2): fixed-travel grommet slots positioned by tube OD ----
  const slotClear=o.slotClear!=null?o.slotClear:0.15, slotKeep=SLOT_W/2+slotClear;
  const slotLen=o.slotLen!=null?o.slotLen:1.5, designPct=o.designPct!=null?o.designPct:0.75;
  const pegR=(o.pegDia!=null?o.pegDia:0.25)/2, rimGap=o.rimGap!=null?o.rimGap:0.1875, halfW=SLOT_W/2;
  const T=o.tubeOD!=null?o.tubeOD:(o.maxOTA||10);
  let travel=slotLen-SLOT_W; if(travel<0.05) travel=0.05;
  let outerC=T/2+(1-designPct)*travel, innerC=outerC-travel;
  if(innerC-halfW<0.25){innerC=0.25+halfW; travel=outerC-innerC;}      // auto-shrink for tiny masks
  const r_outTip=outerC+halfW, R=outerC+(o.rimGap!=null?o.rimGap:0), Rg=R-edge;
  const maxPegR=outerC-pegR, minPegR=innerC+pegR;
  contours.push({role:"outline",verts:outlineVerts(R,o.crestR||CREST_R)});
  for(const C of SLOT_ANGLES) contours.push({role:"slot",verts:slotContour(innerC,outerC,C)});
  // slot stadiums + caps (keepout for grating)
  const slotPolys=[]; const slotCircles=[];
  for(const C of SLOT_ANGLES){const u=pol(1,C),A=[innerC*u[0],innerC*u[1]],B=[outerC*u[0],outerC*u[1]];
    slotPolys.push(stadiumPoly(A,B,slotKeep,80)); slotCircles.push([A[0],A[1],slotKeep],[B[0],B[1],slotKeep]);}
  let slotUnion=slotPolys[0]; for(let i=1;i<slotPolys.length;i++) slotUnion=UN(slotUnion,slotPolys[i]);
  const disc=circlePoly(0,0,Rg,256);

  function addBoolean(rectRing,region,circles){
    let geom=[[rectRing]];
    if(region) geom=IN(geom,region);
    geom=IN(geom,disc);
    geom=DF(geom,slotUnion);
    if(!geom||!geom.length) return;
    for(const poly of geom){const ring=poly[0].slice(0,-1); if(thinFrag(ring,minFrag,minThk)||outBound(ring,Rg+0.06)) continue;
      contours.push({role:"slit",verts:refit(ring,circles,centroid(ring))});}
  }

  if(maskType==="classic"){
    const circles=[[0,0,Rg],...slotCircles];
    const REG={low:[[0,-1,-divH]],ul:[[-1,0,-divV],[0,1,-divH]],ur:[[1,0,-divV],[0,1,-divH]]};
    function rimB(verts,i){const A=[verts[i].x,verts[i].y],B=[verts[(i+1)%verts.length].x,verts[(i+1)%verts.length].y];
      if(Math.abs(hyp(A[0],A[1])-Rg)<3e-3&&Math.abs(hyp(B[0],B[1])-Rg)<3e-3){const mid=[(A[0]+B[0])/2,(A[1]+B[1])/2],dm=hyp(mid[0],mid[1])||1;return arcBulge(A,B,[0,0],[mid[0]/dm*Rg,mid[1]/dm*Rg]);}return 0;}
    function emit(P0,u,reg){const nrm=[-u[1],u[0]],L=R+1;
      const P=(t,off)=>[P0[0]+t*u[0]+off*nrm[0],P0[1]+t*u[1]+off*nrm[1]];
      let verts=[P(-L,hw),P(L,hw),P(L,-hw),P(-L,-hw)];
      for(const[a,b,c] of REG[reg]) verts=clipHP(verts,a,b,c);
      if(verts.length<3) return;
      const rectPoly=[[ [...verts.map(v=>[v[0],v[1]]), verts[0]] ]];
      const touches=IN(rectPoly,slotUnion).length>0;
      if(!touches){let v2=clipCircleIn(verts,0,0,Rg);if(v2.length<3||thinFrag(v2,minFrag,minThk)||outBound(v2,Rg+0.06))return;
        contours.push({role:"slit",verts:v2.map((p,i)=>({x:p[0],y:p[1],b:rimB(v2.map(q=>({x:q[0],y:q[1]})),i)}))});return;}
      addBoolean([...verts.map(v=>[v[0],v[1]]),verts[0]],null,circles);
    }
    const th=(o.angleFromVert!=null?o.angleFromVert:ANGLE_FROM_VERT)*D2R;
    let k=0;while(k*period<=R+1){const xs=k===0?[0]:[k*period,-k*period];for(const sx of xs)emit([sx,0],[0,1],"low");k++;}
    const dR=[Math.sin(th),Math.cos(th)],perpR=[-dR[1],dR[0]],a0=divV*perpR[0]+divH*perpR[1];
    let m=-Math.floor(2*R/period)-2;while(m*period<=2*R+1){const off=a0+m*period,P0=[off*perpR[0],off*perpR[1]];emit(P0,dR,"ur");emit([-P0[0],P0[1]],[-dR[0],dR[1]],"ul");m++;}
  } else if(maskType==="tri_v2"){
    const hubMin=o.hubMin!=null?o.hubMin:0.28;
    const reqHub=(o.hubR!=null?o.hubR:(o.centralObstructionIn!=null?o.centralObstructionIn/2:0));
    const r_in_g=Math.max(reqHub,hubMin);
    // slot keep-out is identical to the Classic mask: the plain stadium gives a concentric
    // semicircular inner cap (radius slotKeep, centered on the slot's inner end).
    emitTriGrating(contours,R,Rg,r_in_g,slotUnion,slotCircles,period,hw,slitW,minFrag,minThk,o);
  }
  return finalize(contours,{R,Rg,period,slitW,type:maskType,maskOD:2*R,designTubeOD:T,
    tubeMinOD:2*minPegR,tubeMaxOD:2*maxPegR,slotLen,slotW:SLOT_W,angleFromVert:(o.angleFromVert!=null?o.angleFromVert:ANGLE_FROM_VERT),
    maxPegR,minPegR,outerC,innerC},o);
}
return {genMask, flatten, SLOT_ANGLES};
});
